import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';

const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange, disabled = false, singleCalendar = false, hideLabels = false, inline = false }) => {
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [awaitingEnd, setAwaitingEnd] = useState(false);
  // 단일 캘린더 임시 선택 상태 및 드래그 상태
  const [tempStart, setTempStart] = useState('');
  const [tempEnd, setTempEnd] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [mouseIsDown, setMouseIsDown] = useState(false);
  const [dragAnchor, setDragAnchor] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [suppressNextClick, setSuppressNextClick] = useState(false);

  // 날짜 포맷팅 함수
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = fromYMD(dateStr) || new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const weekday = weekdays[date.getDay()];
    return `${year}.${month}.${day} (${weekday})`;
  };

  // 로컬 타임존 안전 YYYY-MM-DD 생성
  const toYMD = (date) => {
    if (!(date instanceof Date)) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // YYYY-MM-DD 문자열을 로컬 Date로 안전하게 변환
  const fromYMD = (ymd) => {
    if (!ymd || typeof ymd !== 'string') return null;
    const parts = ymd.split('-');
    if (parts.length !== 3) return null;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    return new Date(y, m - 1, d);
  };

  const getOrderedRange = (startStr, endStr) => {
    if (!startStr) return { start: '', end: '' };
    if (!endStr) return { start: startStr, end: '' };
    const start = fromYMD(startStr);
    const end = fromYMD(endStr);
    if (!start || !end) return { start: startStr, end: endStr };
    if (start <= end) return { start: startStr, end: endStr };
    return { start: endStr, end: startStr };
  };

  const getRangeDayCount = (startStr, endStr) => {
    const start = fromYMD(startStr);
    const end = fromYMD(endStr);
    if (!start || !end) return 0;
    const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff + 1 : 0;
  };

  const closeUnifiedCalendar = () => {
    setTempStart(startDate || '');
    setTempEnd(endDate || '');
    setAwaitingEnd(false);
    setIsDragging(false);
    setDragAnchor('');
    setShowCalendar(false);
  };

  // 단일 캘린더 본문 (시작/종료 한 번에 선택)
  const renderUnifiedRangeSummary = () => {
    const { start, end } = getOrderedRange(tempStart, tempEnd);
    const awaitingEndSelection = awaitingEnd && tempStart && (!tempEnd || tempStart === tempEnd);
    const dayCount = start && end && start !== end ? getRangeDayCount(start, end) : 0;

    return (
      <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2.5">
        <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold text-blue-600 mb-0.5">시작일</div>
            <div className={`text-xs font-semibold leading-snug ${tempStart ? 'text-gray-900' : 'text-gray-400'}`}>
              {tempStart ? formatDate(start) : '선택하세요'}
            </div>
          </div>
          <span className="text-gray-400 text-sm pt-4">~</span>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold text-blue-600 mb-0.5">종료일</div>
            <div className={`text-xs font-semibold leading-snug ${
              awaitingEndSelection ? 'text-gray-400' : tempEnd ? 'text-gray-900' : 'text-gray-400'
            }`}>
              {awaitingEndSelection ? '선택하세요' : tempEnd ? formatDate(end) : '선택하세요'}
            </div>
          </div>
        </div>
        {dayCount > 1 && (
          <div className="mt-2 text-[10px] font-medium text-blue-700">
            총 {dayCount}일
          </div>
        )}
      </div>
    );
  };

  const renderUnifiedCalendarBody = () => {
    return (
      <>
        {renderUnifiedRangeSummary()}

        {/* 월 네비게이션 */}
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-base font-bold text-gray-800">
            {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
          </div>
          <button type="button" onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['일', '월', '화', '수', '목', '금', '토'].map((day, idx) => (
            <div key={day} className={`text-center text-xs font-semibold py-2 ${idx === 0 ? 'text-red-500' : idx === 6 ? 'text-blue-500' : 'text-gray-600'}`}>
              {day}
            </div>
          ))}
        </div>

        {/* 날짜 그리드 */}
        <div
          className="grid grid-cols-7 gap-1"
          onMouseLeave={() => {
            if (awaitingEnd && tempStart && !isDragging && !mouseIsDown) {
              setTempEnd(tempStart);
            }
          }}
        >
          {calendarDays.map((dayInfo, idx) => {
            const isStart = isSameDate(dayInfo.date, tempStart);
            const isEnd = isSameDate(dayInfo.date, tempEnd) && !isStart;
            const inRange = isInRange(dayInfo.date);
            const isToday = isSameDate(dayInfo.date, today);
            const isSunday = dayInfo.date.getDay() === 0;
            const isSaturday = dayInfo.date.getDay() === 6;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleUnifiedDateClick(dayInfo.date)}
                onMouseDown={() => onDayMouseDown(dayInfo.date)}
                onMouseEnter={() => onDayMouseEnterWrapper(dayInfo.date)}
                onMouseUp={onDayMouseUp}
                className={`
                  relative h-9 text-sm font-medium rounded-lg transition-all
                  ${!dayInfo.isCurrentMonth && !isStart && !isEnd && !inRange ? 'text-gray-400 hover:bg-blue-50' : ''}
                  ${dayInfo.isCurrentMonth && !isStart && !isEnd && !inRange ? 'hover:bg-blue-50' : ''}
                  ${isStart || isEnd ? 'bg-blue-600 text-white font-bold shadow-lg' : ''}
                  ${!isStart && !isEnd && inRange ? 'bg-blue-100 text-blue-700' : ''}
                  ${!isStart && !isEnd && !inRange && dayInfo.isCurrentMonth && isSunday ? 'text-red-500' : ''}
                  ${!isStart && !isEnd && !inRange && dayInfo.isCurrentMonth && isSaturday ? 'text-blue-500' : ''}
                  ${!isStart && !isEnd && !inRange && dayInfo.isCurrentMonth && !isSunday && !isSaturday ? 'text-gray-700' : ''}
                  ${isToday && !(isStart || isEnd) ? 'ring-2 ring-blue-400 ring-inset' : ''}
                `}
              >
                {dayInfo.day}
                {(isStart || isEnd) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
        {/* 액션 버튼: 취소/확인 */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2 justify-end">
          <button
            type="button"
            onClick={closeUnifiedCalendar}
            className="px-3 py-2 text-xs font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            취소
          </button>
          <button
            type="button"
            disabled={!tempStart || !tempEnd}
            onClick={() => {
              if (tempStart && tempEnd) {
                const { start, end } = getOrderedRange(tempStart, tempEnd);
                onStartDateChange(start);
                onEndDateChange(end);
              }
              setAwaitingEnd(false);
              setIsDragging(false);
              setDragAnchor('');
              setShowCalendar(false);
            }}
            className="px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg"
          >
            확인
          </button>
        </div>
      </>
    );
  };

  const renderUnifiedCalendarModal = () => {
    if (!showCalendar) return null;
    return createPortal(
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/40"
          onClick={closeUnifiedCalendar}
          aria-hidden="true"
        />
        <div
          className="relative bg-white border-2 border-gray-200 rounded-xl shadow-2xl p-4 w-80 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="기간 선택"
        >
          <div className="text-sm font-bold text-gray-800 mb-3">기간 선택</div>
          {renderUnifiedCalendarBody()}
        </div>
      </div>,
      document.body
    );
  };

  // 달력 데이터 생성
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // 이번 달의 첫날과 마지막 날
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 첫 주의 시작 (일요일부터 시작)
    const startDay = firstDay.getDay();
    
    // 이전 달의 마지막 날
    const prevLastDay = new Date(year, month, 0);
    
    const days = [];
    
    // 이전 달 날짜들
    for (let i = startDay - 1; i >= 0; i--) {
      const day = prevLastDay.getDate() - i;
      days.push({
        day,
        date: new Date(year, month - 1, day),
        isCurrentMonth: false,
        isPrevMonth: true,
      });
    }
    
    // 이번 달 날짜들
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({
        day,
        date: new Date(year, month, day),
        isCurrentMonth: true,
        isPrevMonth: false,
      });
    }
    
    // 다음 달 날짜들 (6주 채우기)
    const remainingDays = 42 - days.length; // 6주 * 7일
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
        isPrevMonth: false,
      });
    }
    
    return days;
  }, [currentMonth]);

  // 날짜 비교 함수
  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = (date1 instanceof Date) ? date1 : fromYMD(date1) || new Date(date1);
    const d2 = (date2 instanceof Date) ? date2 : fromYMD(date2) || new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // 날짜가 범위 내에 있는지 확인 (단일/일반 모드 분기)
  const isInRange = (date) => {
    const d = (date instanceof Date) ? date : new Date(date);
    if (singleCalendar) {
      if (!tempStart || !tempEnd) return false;
      const start = fromYMD(tempStart) || new Date(tempStart);
      const end = fromYMD(tempEnd) || new Date(tempEnd);
      const rangeStart = start <= end ? start : end;
      const rangeEnd = start <= end ? end : start;
      return d >= rangeStart && d <= rangeEnd;
    }
    if (!startDate || !endDate) return false;
    const start = fromYMD(startDate) || new Date(startDate);
    const end = fromYMD(endDate) || new Date(endDate);
    return d >= start && d <= end;
  };

  const navigateToMonthIfNeeded = (date) => {
    setCurrentMonth((prev) => {
      if (date.getFullYear() === prev.getFullYear() && date.getMonth() === prev.getMonth()) return prev;
      return new Date(date.getFullYear(), date.getMonth(), 1);
    });
  };

  // 날짜 선택 핸들러
  const handleDateClick = (date, isForStart) => {
    const dateStr = toYMD(date);
    navigateToMonthIfNeeded(date);
    
    if (isForStart) {
      onStartDateChange(dateStr);
      // 시작일이 종료일보다 늦으면 종료일도 같이 변경
      if (endDate && (fromYMD(dateStr) || new Date(dateStr)) > (fromYMD(endDate) || new Date(endDate))) {
        onEndDateChange(dateStr);
      }
      setShowStartCalendar(false);
    } else {
      // 종료일이 시작일보다 빠르면 시작일도 같이 변경
      if (startDate && (fromYMD(dateStr) || new Date(dateStr)) < (fromYMD(startDate) || new Date(startDate))) {
        onStartDateChange(dateStr);
      }
      onEndDateChange(dateStr);
      setShowEndCalendar(false);
    }
  };

  const handleUnifiedDateClick = (date) => {
    if (isDragging) return; // 드래그 중에는 클릭 사이클 무시
    if (suppressNextClick) { setSuppressNextClick(false); return; }
    const dateStr = toYMD(date);
    navigateToMonthIfNeeded(date);
    // 1st click: range 시작
    if (!tempStart || (tempStart && tempEnd && !awaitingEnd)) {
      setTempStart(dateStr);
      setTempEnd(dateStr);
      setAwaitingEnd(true);
      return;
    }
    // 2nd click: range 종료
    if (awaitingEnd) {
      const start = fromYMD(tempStart) || new Date(tempStart);
      const picked = fromYMD(dateStr) || new Date(dateStr);
      if (picked < start) {
        setTempEnd(tempStart);
        setTempStart(dateStr);
      } else {
        setTempEnd(dateStr);
      }
      setAwaitingEnd(false);
    }
  };

  // 드래그 기반 선택 (단일 캘린더 모드 전용)
  const onDayMouseDown = (date) => {
    if (!singleCalendar) return;
    const ds = toYMD(date);
    // Drag anchor만 지정, 실제 드래그 시작 여부는 MouseEnter에서 결정
    setMouseIsDown(true);
    setIsDragging(false);
    if (awaitingEnd && tempStart) {
      // 두 번째 선택 단계: 기존 시작일을 앵커로 사용, 임시값은 변경하지 않음
      setDragAnchor(tempStart);
    } else {
      // 새로운 선택 시작
      setDragAnchor(ds);
    }
  };
  const onDayMouseEnter = (date) => {
    if (!singleCalendar || !isDragging || !dragAnchor) return;
    const ds = toYMD(date);
    const a = fromYMD(dragAnchor) || new Date(dragAnchor);
    const b = fromYMD(ds) || new Date(ds);
    const start = a <= b ? dragAnchor : ds;
    const end = a <= b ? ds : dragAnchor;
    setTempStart(start);
    setTempEnd(end);
  };
  // 드래그 시작 트리거: 앵커 설정 이후, 다른 셀로 진입하면 드래그 활성화
  const onDayMouseEnterWrapper = (date) => {
    if (!singleCalendar) return;
    const ds = toYMD(date);

    // 1) 종료일 선택 대기 중: 시작일은 고정, 호버한 날짜만 종료일 미리보기
    if (awaitingEnd && tempStart && !mouseIsDown && !isDragging) {
      setTempEnd(ds);
      return;
    }

    // 2) 드래그 시작 처리: 앵커 설정 이후, 다른 셀로 진입하면 드래그 활성화
    if (!dragAnchor || !mouseIsDown) return;
    if (!isDragging && ds !== dragAnchor) {
      setIsDragging(true);
      // 드래그가 시작되는 시점에 임시 선택 초기화
      const a = fromYMD(dragAnchor) || new Date(dragAnchor);
      const b = fromYMD(ds) || new Date(ds);
      const start = a <= b ? dragAnchor : ds;
      const end = a <= b ? ds : dragAnchor;
      setTempStart(start);
      setTempEnd(end);
    }
    onDayMouseEnter(date);
  };
  const onDayMouseUp = () => {
    if (!singleCalendar) return;
    // 실제 드래그가 있었을 때만 종료로 간주하여 awaitingEnd 해제
    if (isDragging) {
      setAwaitingEnd(false);
      setSuppressNextClick(true); // 드래그 직후 발생하는 클릭은 무시해 리셋 방지
    }
    setIsDragging(false);
    setMouseIsDown(false);
  };

  // 이전/다음 달로 이동
  const changeMonth = (offset) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  // 오늘 날짜
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 달력 렌더링
  const renderCalendar = (isForStart) => {
    const selectedDate = isForStart ? startDate : endDate;
    
    return (
      <div className="absolute z-30 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl p-4 w-80">
        {/* 월 네비게이션 */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="text-base font-bold text-gray-800">
            {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
          </div>
          
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['일', '월', '화', '수', '목', '금', '토'].map((day, idx) => (
            <div
              key={day}
              className={`text-center text-xs font-semibold py-2 ${
                idx === 0 ? 'text-red-500' : idx === 6 ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* 날짜 그리드 */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayInfo, idx) => {
            const isSelected = isSameDate(dayInfo.date, selectedDate);
            const isStart = isSameDate(dayInfo.date, startDate);
            const isEnd = isSameDate(dayInfo.date, endDate);
            const inRange = isInRange(dayInfo.date);
            const isToday = isSameDate(dayInfo.date, today);
            const isSunday = dayInfo.date.getDay() === 0;
            const isSaturday = dayInfo.date.getDay() === 6;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleDateClick(dayInfo.date, isForStart)}
                className={`
                  relative h-9 text-sm font-medium rounded-lg transition-all
                  ${!dayInfo.isCurrentMonth && !isSelected && !inRange ? 'text-gray-400 hover:bg-blue-50' : ''}
                  ${dayInfo.isCurrentMonth && !isSelected && !inRange ? 'hover:bg-blue-50' : ''}
                  ${isSelected ? 'bg-blue-600 text-white font-bold shadow-lg' : ''}
                  ${!isSelected && inRange ? 'bg-blue-100 text-blue-700' : ''}
                  ${!isSelected && !inRange && dayInfo.isCurrentMonth && isSunday ? 'text-red-500' : ''}
                  ${!isSelected && !inRange && dayInfo.isCurrentMonth && isSaturday ? 'text-blue-500' : ''}
                  ${!isSelected && !inRange && dayInfo.isCurrentMonth && !isSunday && !isSaturday ? 'text-gray-700' : ''}
                  ${isToday && !isSelected ? 'ring-2 ring-blue-400 ring-inset' : ''}
                `}
              >
                {dayInfo.day}
                {(isStart || isEnd) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>

        {/* 빠른 선택 버튼 */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
          <button
            type="button"
            onClick={() => {
              const todayStr = today.toISOString().split('T')[0];
              handleDateClick(today, isForStart);
            }}
            className="flex-1 px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            오늘
          </button>
          {isForStart && (
            <button
              type="button"
              onClick={() => {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                handleDateClick(tomorrow, false);
                setShowStartCalendar(false);
              }}
              className="flex-1 px-3 py-2 text-xs font-medium bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
            >
              내일까지
            </button>
          )}
        </div>
      </div>
    );
  };

  if (singleCalendar) {
    return (
      <div className="relative">
        
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setShowCalendar(!showCalendar);
              setShowStartCalendar(false);
              setShowEndCalendar(false);
              const base = startDate || endDate ? (startDate || endDate) : new Date().toISOString().split('T')[0];
              setCurrentMonth(new Date(base));
              setAwaitingEnd(false);
              // 임시 상태 동기화
              setTempStart(startDate || '');
              setTempEnd(endDate || '');
              setIsDragging(false);
              setDragAnchor('');
              setSuppressNextClick(false);
              setMouseIsDown(false);
            }
          }}
          disabled={disabled}
          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-sm text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all hover:border-blue-300 disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between group"
        >
          <span className={startDate ? 'text-gray-900 font-medium' : 'text-gray-400'}>
            {startDate && endDate
              ? `${formatDate(startDate)} ~ ${formatDate(endDate)}`
              : '기간을 선택하세요'}
          </span>
          <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        {renderUnifiedCalendarModal()}
      </div>
    );
  }

  return (
    <div className={inline ? 'flex items-center gap-2' : 'grid grid-cols-2 gap-4'}>
      {/* 시작일 */}
      <div className={`relative ${inline ? 'min-w-[148px]' : ''}`}>
        {!hideLabels && <label className="block text-sm font-semibold text-gray-700 mb-2">시작일 *</label>}
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setShowStartCalendar(!showStartCalendar);
              setShowEndCalendar(false);
              setCurrentMonth(startDate ? new Date(startDate) : new Date());
            }
          }}
          disabled={disabled}
          className={`w-full text-sm text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all hover:border-blue-300 disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between group ${
            inline
              ? 'h-9 border border-gray-300 rounded-md px-2 bg-white'
              : 'border-2 border-gray-200 rounded-lg px-4 py-2.5'
          }`}
        >
          <span className={`truncate ${startDate ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
            {startDate ? formatDate(startDate) : (hideLabels ? '시작일' : '시작일을 선택하세요')}
          </span>
          <svg className={`flex-shrink-0 text-gray-400 group-hover:text-blue-500 transition-colors ${inline ? 'w-4 h-4 ml-1' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        {showStartCalendar && renderCalendar(true)}
      </div>

      {inline && <span className="text-xs text-gray-500">~</span>}

      {/* 종료일 */}
      <div className={`relative ${inline ? 'min-w-[148px]' : ''}`}>
        {!hideLabels && <label className="block text-sm font-semibold text-gray-700 mb-2">종료일 *</label>}
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setShowEndCalendar(!showEndCalendar);
              setShowStartCalendar(false);
              setCurrentMonth(endDate ? new Date(endDate) : startDate ? new Date(startDate) : new Date());
            }
          }}
          disabled={disabled}
          className={`w-full text-sm text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all hover:border-blue-300 disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between group ${
            inline
              ? 'h-9 border border-gray-300 rounded-md px-2 bg-white'
              : 'border-2 border-gray-200 rounded-lg px-4 py-2.5'
          }`}
        >
          <span className={`truncate ${endDate ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
            {endDate ? formatDate(endDate) : (hideLabels ? '종료일' : '종료일을 선택하세요')}
          </span>
          <svg className={`flex-shrink-0 text-gray-400 group-hover:text-blue-500 transition-colors ${inline ? 'w-4 h-4 ml-1' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        {showEndCalendar && renderCalendar(false)}
      </div>

      {/* 달력 외부 클릭 시 닫기 */}
      {(showStartCalendar || showEndCalendar) && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => {
            setShowStartCalendar(false);
            setShowEndCalendar(false);
          }}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
